import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { ACTION_HISTORY_QUERY_POLICY } from 'api/queryPolicies'
import useStore from 'state/knovStore'

export const PEOPLE_CHATS_QUERY_KEY = ['peopleChats'] as const

const peopleChatsQueryKey = (spaceId: string | null) => [...PEOPLE_CHATS_QUERY_KEY, spaceId]

type PersonChat = {
    user_id: string
    // The other person (Users.api_template shape) for the group header.
    user?: any
    count: number
    // Threads shared with this person, newest interaction first; each carries
    // their latest answer or the viewer's latest mention for preview + permalink.
    // Quests are slim sidebar previews (id, content, flags), NOT full payloads —
    // do not seed the quest cache with them.
    chats: { quest: any; answer: any; created_at: string }[]
}

type ResponseData = {
    people: PersonChat[]
    has_more?: boolean
}

export default function useGetPeopleChats(enabled: boolean = true) {
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const shouldFetch = enabled && Boolean(activeSpaceId)

    const result = useInfiniteQuery({
        queryKey: peopleChatsQueryKey(activeSpaceId),
        queryFn: async ({ pageParam = 1 }) => {
            const page = typeof pageParam === 'number' ? pageParam : Number(pageParam) || 1
            const data: ResponseData = await api.getPeopleChats(activeSpaceId, { page })
            return data
        },
        enabled: shouldFetch,
        staleTime: ACTION_HISTORY_QUERY_POLICY.staleTime,
        gcTime: ACTION_HISTORY_QUERY_POLICY.gcTime,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => (lastPage.has_more ? pages.length + 1 : undefined),
    })

    const people = result.data?.pages.flatMap(page => page.people) || []

    return {
        ...result,
        people,
    }
}
